/* eslint-disable react/no-unescaped-entities */
/**
 * HelpCenter — single index for every tutorial + on-page tour in the app.
 *
 * Rows are grouped by product area (Dashboard, Learning, Community).
 * Each entry is a card with either:
 *   - A "Read tutorial" link → deep-links to the written /tutorial/*
 *   - A "Play the tour" button → clears the tour's localStorage flag and
 *     navigates to the page so the on-page tour auto-runs
 *
 * The localStorage keys must match the ones used by each tour file:
 *   - dashboard_tour_seen_v1
 *   - learning_tour_seen_v2
 *   - lesson_tour_seen_v1
 *   - community_tour_seen_v1
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ToolSelector from '../components/ToolSelector';
import { Button } from '../components/ui/button';
import {
  BookOpen, GraduationCap, MessageCircle, LayoutDashboard, PlayCircle,
  ArrowRight, Sparkles, LifeBuoy, Users, Bug, ExternalLink, Activity,
} from 'lucide-react';

// Public URL of the uptime/status page. Placeholder until BetterStack /
// UptimeRobot public status page is wired up — see /app/docs/UPTIME_MONITORING.md.
const STATUS_PAGE_URL = 'https://status.freezedry.academy';

const AREAS = [
  {
    id: 'dashboard',
    title: 'Dashboard',
    color: 'text-primary bg-primary/15',
    Icon: LayoutDashboard,
    description: 'The workbench you land on every day — batch shortcuts, progress, notifications, DMs, tour.',
    tour: {
      label: 'Play the Dashboard tour',
      storageKey: 'dashboard_tour_seen_v1',
      href: '/dashboard',
    },
  },
  {
    id: 'learning',
    title: 'Learning',
    color: 'text-emerald-600 bg-emerald-500/15',
    Icon: GraduationCap,
    description: 'Catalog, tracks, courses, quizzes, certificates, streaks, and the AI Teacher planner.',
    tutorial: {
      label: 'Read the Learning tutorial',
      href: '/tutorial/learning',
      subtitle: '17-section written walkthrough · ~15 min read',
    },
    tour: {
      label: 'Play the Learning tour',
      storageKey: 'learning_tour_seen_v2',
      href: '/learning',
    },
  },
  {
    id: 'freedriq',
    title: 'FreedrIQ diagnostic',
    color: 'text-sky-600 bg-sky-500/15',
    Icon: Sparkles,
    description: 'The pre/post skills assessment — 4 tiers, growth reports, study plans, and the Community Challenge.',
    tutorial: {
      label: 'Read the FreedrIQ tutorial',
      href: '/tutorial/freedriq',
      subtitle: '7-section written walkthrough · ~8 min read',
    },
  },
  {
    id: 'tools',
    title: 'Tools (calculators)',
    color: 'text-primary bg-primary/15',
    Icon: LayoutDashboard,
    description: 'Household batch calc, business ProForma, multi-machine capacity, sensitivity tornado, and the AI business summary — all under the Tools tab.',
    tutorial: {
      label: 'Read the Tools tutorial',
      href: '/tutorial/tools',
      subtitle: '6-section written walkthrough · ~6 min read',
    },
  },
  {
    id: 'lesson',
    title: 'Inside a lesson',
    color: 'text-amber-600 bg-amber-500/15',
    Icon: PlayCircle,
    description: 'How to use the lesson tools — progress, notes, materials, Mark Complete, anti-cheat.',
    tour: {
      label: 'Play the Lesson tour',
      storageKey: 'lesson_tour_seen_v1',
      href: '/learning',
      hint: 'Opens Learning — jump into any lesson and the tour auto-runs.',
    },
  },
  {
    id: 'community',
    title: 'Community forum',
    color: 'text-rose-600 bg-rose-500/15',
    Icon: MessageCircle,
    description: 'Categories, replies, reactions, AI Ambassador, DMs, moderation, ToS.',
    tutorial: {
      label: 'Read the Community tutorial',
      href: '/tutorial/community',
      subtitle: '10-section written walkthrough · ~10 min read',
    },
    tour: {
      label: 'Play the Community tour',
      storageKey: 'community_tour_seen_v1',
      href: '/community',
    },
  },
];

const HelpCenter = () => {
  const navigate = useNavigate();

  const replayTour = (storageKey, href) => {
    try { window.localStorage.removeItem(storageKey); } catch { /* ignore */ }
    navigate(href);
  };

  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center gap-3">
          <div className="h-11 w-11 rounded-full bg-primary/15 text-primary flex items-center justify-center">
            <LifeBuoy className="w-5 h-5" />
          </div>
          <div>
            <div className="eyebrow text-primary">Help center</div>
            <h1 className="font-heading text-4xl font-extrabold text-foreground tracking-tight">
              Every tutorial + tour in one place
            </h1>
            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
              Written guides for a deep dive, on-page tours for a quick refresher. Replay any tour any time — we'll reset the "first visit" flag and drop you on the page so it auto-runs.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="help-center-grid">
          {AREAS.map((area) => {
            const { Icon } = area;
            return (
              <section
                key={area.id}
                className="pro-card p-5 flex flex-col gap-3"
                data-testid={`help-area-${area.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center flex-shrink-0 ${area.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-lg font-extrabold text-foreground tracking-tight">
                      {area.title}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{area.description}</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  {area.tutorial && (
                    <Link
                      to={area.tutorial.href}
                      className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 hover:bg-muted px-3 py-2 transition-colors"
                      data-testid={`help-tutorial-${area.id}`}
                    >
                      <BookOpen className="w-4 h-4 text-primary flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-foreground">{area.tutorial.label}</div>
                        {area.tutorial.subtitle && (
                          <div className="text-[11px] text-muted-foreground truncate">{area.tutorial.subtitle}</div>
                        )}
                      </div>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    </Link>
                  )}
                  {area.tour && (
                    <Button
                      onClick={() => replayTour(area.tour.storageKey, area.tour.href)}
                      variant="outline"
                      className="justify-start rounded-xl h-auto py-2 gap-3"
                      data-testid={`help-tour-${area.id}`}
                    >
                      <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
                      <div className="min-w-0 flex-1 text-left">
                        <div className="text-sm font-bold text-foreground">{area.tour.label}</div>
                        <div className="text-[11px] text-muted-foreground truncate font-normal">
                          {area.tour.hint || 'Replays the on-page coach marks.'}
                        </div>
                      </div>
                      <PlayCircle className="w-4 h-4 text-primary flex-shrink-0" />
                    </Button>
                  )}
                </div>
              </section>
            );
          })}
        </div>

        <div className="mt-8 pro-card p-6" data-testid="help-support-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center">
              <LifeBuoy className="w-4 h-4" />
            </div>
            <div>
              <div className="font-heading text-lg font-extrabold text-foreground">Need a hand?</div>
              <p className="text-xs text-muted-foreground">
                Pick the fastest lane for what you need.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* Support ticket */}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('fda:open-report', { detail: { type: 'support' } }))}
              className="text-left rounded-xl border border-border bg-muted/30 hover:bg-muted transition-colors p-4"
              data-testid="help-open-ticket"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <LifeBuoy className="w-4 h-4 text-primary" />
                <div className="text-sm font-extrabold text-foreground">Submit a ticket</div>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Anything account, billing, or how-do-I. We reply by email, usually within one business day.
              </p>
            </button>

            {/* Bug report */}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('fda:open-report', { detail: { type: 'bug' } }))}
              className="text-left rounded-xl border border-border bg-muted/30 hover:bg-muted transition-colors p-4"
              data-testid="help-open-bug"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Bug className="w-4 h-4 text-rose-500" />
                <div className="text-sm font-extrabold text-foreground">Report a bug</div>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Broken UI, error message, something not adding up? Screenshot welcome.
              </p>
            </button>

            {/* Status page (external) */}
            <a
              href={STATUS_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-xl border border-border bg-muted/30 hover:bg-muted transition-colors p-4"
              data-testid="help-service-status"
            >
              <div className="flex items-center gap-2 mb-1.5">
                <Activity className="w-4 h-4 text-emerald-600" />
                <div className="text-sm font-extrabold text-foreground flex items-center gap-1">
                  Service status <ExternalLink className="w-3 h-3 text-muted-foreground" />
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Check if we're currently having any incidents before opening a ticket.
              </p>
            </a>
          </div>

          <div className="mt-5 pt-5 border-t border-border flex items-center gap-3 flex-wrap">
            <Users className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-xs text-muted-foreground min-w-0 flex-1 min-w-[180px]">
              Prefer to ask peers first? The Community forum answers most operator questions faster than we can.
            </span>
            <Button
              onClick={() => navigate('/community')}
              variant="outline"
              className="rounded-full font-bold"
              data-testid="help-open-community"
            >
              Open Community <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
            <Button
              onClick={() => navigate('/messages')}
              className="rounded-full font-bold"
              data-testid="help-open-messages"
            >
              <MessageCircle className="w-4 h-4 mr-1" /> Direct message
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
